import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "@tanstack/react-router";
import type { TenantSettings } from "@praesto/contracts";
import { useCore } from "../lib/core";

/**
 * Mandatory-onboarding gate. Mounted once inside `AppLayout` so it runs for
 * every authed route (the shell renders for the whole `/$tenantSlug/*` tree).
 *
 * The guard condition is deliberately `settings.onboarding?.status ===
 * "pending"` — an explicit check for "pending" — and NOT
 * `!== "completed"`. Every tenant provisioned before this field existed (the
 * live BlackFin customer included) AND every superadmin-provisioned tenant
 * (`functions/src/lib/provision-tenant.ts` writes `{ status: "completed" }`
 * for `via: "superadmin"`) has `onboarding` either absent or `"completed"`.
 * A `!== "completed"` condition would ALSO match `undefined`, forcing every
 * pre-existing owner into this mandatory wizard on their next login — a
 * severe regression. Only tenants explicitly marked `"pending"` (self-service
 * signups, `provisionTenant`'s `via: "self_service"` path) are redirected.
 *
 * Non-owners are never redirected: onboarding is an owner-only setup flow.
 * The `/onboarding` route itself is exempt so the redirect doesn't loop.
 */
export function OnboardingGuard() {
  const core = useCore();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };

  const [settings, setSettings] = useState<TenantSettings | null>(() => {
    try {
      return core.tenant.settings();
    } catch {
      return null;
    }
  });

  useEffect(() => core.tenant.onSettingsChange(setSettings), [core]);

  const isOwner = core.rbac.can("manage", "hotel");
  const isPending = settings?.onboarding?.status === "pending";
  const onboardingPath = `/${tenantSlug}/onboarding`;
  const onOnboardingRoute = location.pathname === onboardingPath;
  // The guide is the second exemption. An owner who has just signed up is
  // exactly the reader who most needs it, and without this they would be
  // bounced back to the wizard from `/help` — the one place that explains the
  // wizard. It is read-only content: letting it through cannot leave the
  // account half-configured.
  const onHelpRoute = location.pathname === `/${tenantSlug}/help`;
  const shouldRedirect = isOwner && isPending && !onOnboardingRoute && !onHelpRoute;

  useEffect(() => {
    if (shouldRedirect) {
      void navigate({ to: onboardingPath, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRedirect, onboardingPath]);

  return null;
}
